"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const phoenixQuery = new WP_Query({
  slug: "phoenix",
  per_page: 1
});
function PhoenixImageConsumer() {
  const {
    products
  } = useEcommerceProducts();
  const product = products[0];
  const imgSrc = product?.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709223/img/phoenix_kitten.webp";
  return <img src={imgSrc} alt="Phoenix - Odd-Eyed Exotic Shorthair Persian Kitten" className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" />;
}
export default function EditorialKittenHero() {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [flameAngle, setFlameAngle] = useState(0);
  useEffect(() => {
    const node = heroRef.current;
    if (!node) return;
    const handleMove = (clientX: number) => {
      const rect = node.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const offset = (clientX - center) / (rect.width / 2);
      const target = Math.max(-4, Math.min(4, offset * 4));
      setFlameAngle(prev => prev + (target - prev) * 0.18);
    };
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("touchmove", onTouchMove);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, []);
  return <section data-section-id="11700" id="editorialkittenhero" ref={heroRef} className="relative w-full bg-background text-foreground overflow-hidden">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-20 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-start">
          <div className="order-2 lg:order-1 lg:col-span-6 lg:pt-6">
            <p className="font-sans uppercase text-[0.8125rem] font-medium text-muted-foreground" style={{
            letterSpacing: "0.14em"
          }}>
              Rare Exotic Kittens
            </p>

            <h1 className="font-serif text-foreground mt-4" style={{
            fontSize: "clamp(2.75rem, 6.4vw, 5.5rem)",
            fontWeight: 360,
            letterSpacing: "-0.02em",
            lineHeight: 0.98
          }}>
              Luxury Kittens for Sale in Tampa, Florida{" "}
              <span className="inline font-normal text-muted-foreground" style={{
              fontSize: "clamp(1.1rem, 2vw, 1.5rem)",
              fontWeight: 400,
              letterSpacing: "normal",
              lineHeight: 1.3
            }}>
                — Nationwide Delivery Available.
              </span>
            </h1>

            <p className="font-serif text-chart-4 mt-6 max-w-xl" style={{
            fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
            fontWeight: 380,
            lineHeight: 1.55
          }}>
              Catsy Cats is a Tampa boutique breeder specializing in rare, hand-raised exotic kittens. We place our cherished kittens with loving families across the nation.
            </p>

            <p className="font-serif text-chart-4 mt-4 max-w-xl" style={{
            fontSize: "clamp(1rem, 1.2vw, 1.125rem)",
            lineHeight: 1.5
          }}>
              Take the first step in your journey to bring one of our kittens home or join our waitlist.
            </p>

            <p className="font-serif text-chart-4 mt-4 max-w-xl" style={{
            fontSize: "clamp(1rem, 1.2vw, 1.125rem)",
            lineHeight: 1.55
          }}>
              Every Catsy Cats kitten is more than a pet — she's the newest member of your family. Hand-raised with round-the-clock love, gently socialized, and vet-checked before she ever meets you, each kitten is ready to curl up on your lap and call your house home. Whether you're welcoming a cherished addition to your family or the one perfect soulmate to greet you every day, your forever baby is waiting.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Button asChild className="group h-auto rounded-lg bg-primary text-primary-foreground hover:bg-[var(--chart-1)] px-7 py-3.5 text-[0.9375rem] font-medium transition-all duration-300" style={{
              letterSpacing: "0.02em"
            }}>
                <Link to="https://form.getformly.com/f/ZvpKxt" newTab>
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    Start Here
                  </span>
                </Link>
              </Button>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-6 relative">
            <div className="relative mx-auto" style={{
            maxWidth: "380px"
          }}>
              <div className="hidden md:flex absolute -left-6 top-10 z-20 items-center gap-2 -rotate-[8deg]" aria-hidden="true">
                <span className="font-mono" style={{
                fontFamily: "Caveat, cursive",
                fontSize: "1.25rem",
                fontWeight: 500,
                color: "var(--chart-1)",
                lineHeight: 1.1
              }}>
                  ← new arrivals
                </span>
              </div>

              <svg width="0" height="0" style={{
              position: "absolute"
            }} aria-hidden="true">
                <defs>
                  <clipPath id="emberBlob" clipPathUnits="objectBoundingBox">
                    <path d="M 0.52,0.02 C 0.72,0.03 0.88,0.13 0.95,0.30 C 1.01,0.46 0.99,0.64 0.92,0.78 C 0.84,0.92 0.69,0.99 0.52,0.99 C 0.34,1.00 0.18,0.94 0.09,0.80 C 0.01,0.66 -0.01,0.47 0.04,0.30 C 0.10,0.13 0.27,0.02 0.45,0.01 C 0.47,0.01 0.50,0.02 0.52,0.02 Z" />
                  </clipPath>
                </defs>
              </svg>

              <div className="relative w-full" style={{
              aspectRatio: "4 / 5",
              clipPath: "url(#emberBlob)",
              WebkitClipPath: "url(#emberBlob)",
              animation: "blobIdle 6s ease-in-out infinite"
            }}>
                <EcommerceProductsProvider wp_query={{
                slug: ["phoenix"],
                per_page: 1
              }}>
                  <PhoenixImageConsumer />
                </EcommerceProductsProvider>
              </div>

              <svg className="absolute pointer-events-none z-10" style={{
              top: "18%",
              left: "30%",
              width: "44px",
              height: "64px",
              transform: `rotate(${flameAngle}deg)`,
              transformOrigin: "50% 95%",
              transition: "transform 120ms linear"
            }} viewBox="0 0 44 64" aria-hidden="true">
                <path d="M22 4 C 28 14, 36 22, 36 36 C 36 50, 30 60, 22 60 C 14 60, 8 50, 8 36 C 8 24, 16 16, 22 4 Z" fill="var(--chart-1)" opacity="0.92" />
                <path d="M22 18 C 26 24, 30 30, 30 40 C 30 50, 26 56, 22 56 C 18 56, 14 50, 14 40 C 14 32, 18 26, 22 18 Z" fill="hsl(45 95% 70%)" opacity="0.85" />
              </svg>

              <p className="mt-6 text-center font-sans text-muted-foreground" style={{
              fontSize: "0.8125rem",
              letterSpacing: "0.01em",
              lineHeight: 1.45
            }}>
                Rare Elf Sphynx kitten, hand-raised in Tampa.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes drawUnderline {
          from { stroke-dashoffset: 320; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes blobIdle {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-4px) scale(1.005); }
        }
      `}</style>
    </section>;
}